import type { InboundShipmentLine } from '@wms/domain';

// Mal kabul: a shipment's lines in two lists, what is still to come above what is in. A line moves
// down as soon as its count reaches the announcement, or a manager says the rest is not coming.

type Counted = Pick<InboundShipmentLine, 'expectedQty' | 'receivedQty' | 'damagedQty' | 'shortMarkedAt'>;

export interface LineLists<L> {
  /** announced, not all counted yet and not marked “Eksik geldi”: a partly counted line stays here with its progress */
  readonly remaining: readonly L[];
  /** complete or over, marked “Eksik geldi”, or not announced and taken in */
  readonly accepted: readonly L[];
  /** units the remaining lines still wait for */
  readonly remainingUnits: number;
  /** units counted on the accepted lines, damaged ones too */
  readonly acceptedUnits: number;
}

/** Whether the dock still waits for units of the line. */
export const stillExpected = (l: Counted): boolean => l.expectedQty > 0 && l.receivedQty + l.damagedQty < l.expectedQty && l.shortMarkedAt === null;

/** The lines split in two, each list in the shipment's order. */
export function splitLines<L extends Counted>(lines: readonly L[]): LineLists<L> {
  const remaining: L[] = [];
  const accepted: L[] = [];
  let remainingUnits = 0;
  let acceptedUnits = 0;
  for (const line of lines) {
    const counted = line.receivedQty + line.damagedQty;
    if (stillExpected(line)) {
      remaining.push(line);
      remainingUnits += line.expectedQty - counted;
    } else {
      accepted.push(line);
      acceptedUnits += counted;
    }
  }
  return { remaining, accepted, remainingUnits, acceptedUnits };
}

/** More accepted lines than this fold away on an open shipment: the dock looks at what is still to come. */
export const ACCEPTED_FOLD_AFTER = 8;

/** Whether the accepted list can fold (and starts folded, unless the device keeps it open). */
export const acceptedFolds = (shipmentOpen: boolean, acceptedLines: number): boolean => shipmentOpen && acceptedLines > ACCEPTED_FOLD_AFTER;
